import { Link } from 'react-router-dom';
import { DASHBOARD_URL } from '../App';

export default function HomePage() {
  return (
    <div>
      {/* Hero Section - starts at top, nav floats over it */}
      <section className="relative overflow-hidden bg-slate-900 pt-32 pb-24 sm:pt-40 sm:pb-32">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Beta Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white/90 px-4 py-2 rounded-full text-sm font-medium mb-8 border border-white/10">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-400"></span>
              </span>
              Now in Beta
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 leading-tight">
              The <span className="text-blue-400">Nexus</span> Between Your ERP
              <br className="hidden sm:block" /> and Modern Interfaces
            </h1>

            <p className="text-xl text-white/60 mb-2">
              <span className="font-semibold text-white/90">nexus</span>{' '}
              <span className="italic">/ˈneksəs/</span> — a connection or series of connections linking two or more things.
            </p>

            <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
              Connect your Odoo ERP to marketplace integrations, advanced
              reporting dashboards, and business tools — without requiring
              Odoo licenses for every user.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`${DASHBOARD_URL}/signup`}
                className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all"
              >
                Get Started
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <Link
                to="/connectors"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all border border-white/20"
              >
                View Connectors
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-20 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-900 mb-2">1</div>
              <div className="text-lg font-semibold text-slate-900 mb-1">Odoo License</div>
              <div className="text-gray-500">Single API connection to your ERP</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-900 mb-2">Unlimited</div>
              <div className="text-lg font-semibold text-slate-900 mb-1">Team Members</div>
              <div className="text-gray-500">No per-seat Odoo fees</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-900 mb-2">100%</div>
              <div className="text-lg font-semibold text-slate-900 mb-1">Data Access</div>
              <div className="text-gray-500">Full ERP connectivity</div>
            </div>
          </div>
        </div>
      </section>

      {/* Connectors Preview */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3">Marketplace Connectors</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Sync your Odoo with leading marketplaces
            </h2>
            <p className="text-lg text-gray-600">
              Push listings, sync inventory, and pull orders from e-commerce platforms directly into your ERP.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* GunBroker */}
            <Link to="/connectors" className="group">
              <div className="bg-white rounded-xl p-8 border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all h-full">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">Live</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">GunBroker</h3>
                <p className="text-gray-600 mb-4">Sync your firearms inventory, manage listings, and import orders.</p>
                <span className="text-sm font-medium text-blue-600 group-hover:underline">Learn more →</span>
              </div>
            </Link>

            {/* eBay */}
            <Link to="/connectors" className="group">
              <div className="bg-white rounded-xl p-8 border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all h-full">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">Live</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">eBay</h3>
                <p className="text-gray-600 mb-4">Create listings, sync quantities, and automatically import orders.</p>
                <span className="text-sm font-medium text-blue-600 group-hover:underline">Learn more →</span>
              </div>
            </Link>

            {/* ShipStation */}
            <Link to="/connectors" className="group">
              <div className="bg-white rounded-xl p-8 border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all h-full">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-full">Coming Soon</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">ShipStation</h3>
                <p className="text-gray-600 mb-4">Streamline fulfillment with automated shipping and tracking sync.</p>
                <span className="text-sm font-medium text-blue-600 group-hover:underline">Learn more →</span>
              </div>
            </Link>
          </div>

          <div className="mt-12 text-center">
            <Link to="/connectors" className="text-blue-600 font-semibold hover:underline">
              View all connectors →
            </Link>
          </div>
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3">Platform Features</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              More than just connectors
            </h2>
            <p className="text-lg text-gray-600">
              Advanced reporting, team management, and enterprise security built for growing businesses.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Link to="/features" className="group">
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all h-full">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-6 border border-gray-200">
                  <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">Advanced Reporting</h3>
                <p className="text-gray-600 mb-4">Real-time P&L dashboards, sales analytics, and inventory reports. Share insights with stakeholders who don't need Odoo access.</p>
                <span className="text-sm font-medium text-blue-600 group-hover:underline">Learn more →</span>
              </div>
            </Link>

            <Link to="/features" className="group">
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all h-full">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-6 border border-gray-200">
                  <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">Team Management</h3>
                <p className="text-gray-600 mb-4">Invite unlimited team members with role-based permissions. Control data access without additional Odoo licenses.</p>
                <span className="text-sm font-medium text-blue-600 group-hover:underline">Learn more →</span>
              </div>
            </Link>
          </div>

          <div className="mt-12 text-center">
            <Link to="/features" className="text-blue-600 font-semibold hover:underline">
              View all features →
            </Link>
          </div>
        </div>
      </section>

      {/* Odoo Compatibility */}
      <section className="py-24 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Works with your entire Odoo system
            </h2>
            <p className="text-lg text-gray-600">
              Enterprise & Community editions, versions 14-18
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {[
              'Sales', 'CRM', 'Inventory', 'Manufacturing', 'Accounting',
              'Point of Sale', 'E-commerce', 'Purchases', 'Invoicing',
              'Project Management', 'HR', 'Helpdesk'
            ].map((module) => (
              <span
                key={module}
                className="bg-white px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200"
              >
                {module}
              </span>
            ))}
            <span className="bg-white px-4 py-2 rounded-lg text-sm font-medium text-gray-500 border border-gray-200">
              + Custom Modules
            </span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to connect your Odoo?
          </h2>
          <p className="text-lg text-white/70 mb-8 max-w-xl mx-auto">
            Join the beta and help shape the future of NexusERP. Early adopters get founding member pricing.
          </p>
          <a
            href={`${DASHBOARD_URL}/signup`}
            className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all"
          >
            Get Started
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </section>
    </div>
  );
}
